import { useState, useEffect, useMemo, useCallback, FormEvent, MouseEvent, ReactElement, ChangeEvent, useRef } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { auth, storage } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
  QuerySnapshot,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreError,
  writeBatch,
  deleteField,
  startAfter,
  increment,
  FieldValue,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db } from "../firebase";
import { Transaction, Account, WorkspacePermissions, WorkspaceRole, WorkspaceSettings as WorkspaceSettingsType, AttachmentHistoryEntry } from "../types";
import { addToCache, getFromCache, mergeCacheWithDatabase, getDemoWorkspaceId } from "../utils/demoMode";
import { DemoModeMessage } from "../utils/demoModeMessage";
import { isBypassAuthEnabled } from "../utils/bypassAuth";
import { api } from "../services/api";
import { TransactionCategory, TransactionCategoryDetail, TransactionDetailValue, TransactionCategoryGroup, TransactionCustomFieldValues } from "../types";
import {
  formatCurrency,
  formatRate,
  formatTimeLocal,
  getCurrentTime,
  combineDateAndTime,
  isSearchByTransactionId,
} from "../utils/transactionUtils";
import {
  processTransaction,
  processTransactionSnapshot,
  parseTransactionDateFromRaw,
  sortTransactionsByDate,
  TransactionWithDate,
} from "../utils/transactionProcessing";
import { useTransactionFilters } from "./Activity/hooks/useTransactionFilters";
import TransactionList from "./Activity/components/TransactionList";
import TransactionDialog from "./Activity/components/TransactionDialog";
import FeeEditDialog, { FeeEditFormData } from "./Activity/components/FeeEditDialog";
import TransactionFilters from "./Activity/components/TransactionFilters";
import TransactionHistoryDialog from "./Activity/components/TransactionHistoryDialog";
import AttachmentPreviewDialog from "./Activity/components/AttachmentPreviewDialog";
import TransactionMenu from "./Activity/components/TransactionMenu";
import TruncatedTextTooltip from "./Activity/components/TruncatedTextTooltip";
import { useTransactions } from "./Activity/hooks/useTransactions";
import { useAccounts } from "./Activity/hooks/useAccounts";
import { useCurrencies } from "./Activity/hooks/useCurrencies";
import { useCategories } from "./Activity/hooks/useCategories";
import { useWorkspaceData, WorkspaceMember } from "./Activity/hooks/useWorkspaceData";
import { useTransactionHistory } from "./Activity/hooks/useTransactionHistory";
import { useTransactionHistoryCheck } from "./Activity/hooks/useTransactionHistoryCheck";
import { useAccountHelpers } from "./Activity/hooks/useAccountHelpers";
import { useCurrencyHelpers } from "./Activity/hooks/useCurrencyHelpers";
import { useTransactionMenuHandlers } from "./Activity/hooks/useTransactionMenuHandlers";
import { useTransactionFormHandlers } from "./Activity/hooks/useTransactionFormHandlers";
import { useTransactionSubmission } from "./Activity/hooks/useTransactionSubmission";
import { useFeeSubmission } from "./Activity/hooks/useFeeSubmission";
import { useActivityHelpers } from "./Activity/hooks/useActivityHelpers";
import { useFormValidation } from "./Activity/hooks/useFormValidation";
import { useFilterHelpers } from "./Activity/hooks/useFilterHelpers";
import { useActivityPeriodFilter } from "./Activity/hooks/useActivityPeriodFilter";
import { useDialogHandlers } from "./Activity/hooks/useDialogHandlers";
import { useTransactionCustomFieldsSettings } from "./TransactionFieldsSettings/hooks/useTransactionCustomFieldsSettings";
import { useCategoryDetails } from "./Activity/hooks/useCategoryDetails";
import { useMenuHandlers } from "./Activity/hooks/useMenuHandlers";
import { usePagination } from "./Activity/hooks/usePagination";
import { useAutoFilterSelection } from "./Activity/hooks/useAutoFilterSelection";
import { useWorkspaceSettings } from "./WorkspaceSettings/hooks/useWorkspaceSettings";
import { useFilterReset } from "./Activity/hooks/useFilterReset";
import { useRateAutoUpdate } from "./Activity/hooks/useRateAutoUpdate";
import { usePermissionHelpers } from "./Activity/hooks/usePermissionHelpers";
import { useActionButtonHandlers } from "./Activity/hooks/useActionButtonHandlers";

import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Typography,
  Paper,
  Stack,
  IconButton,
  Menu,
  Divider,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Chip,
  Grid,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Autocomplete,
  Popover,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Fade,
  Collapse,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import RefreshIconButton from "../components/RefreshIconButton";

// Categories will be loaded from API based on expense/income type
// TRANSACTION_TYPES moved to useTransactionFilters hook


interface ActivityProps {
  workspaceId: string;
  /** When set (e.g. from URL /:workspaceSlug/transaction/:id), open edit dialog in view-only mode for this transaction. */
  initialViewTransactionId?: string;
  initialFilters?: {
    category?: string;
    account?: string;
    users?: string[];
    startDate?: string;
    endDate?: string;
    typeFilter?: string;
  };
}

interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: TransactionDetailValue;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

// WorkspaceMember interface moved to useWorkspaceData hook

// TransactionWithDate interface moved to transactionProcessing.ts

// TransactionHistoryEntry interface moved to useTransactionHistory hook


function Activity({ workspaceId, initialViewTransactionId, initialFilters }: ActivityProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  
  // All hooks must be declared first (React Hooks rules)
  
  // Data fetching hooks
  const handleMembersLoaded = useCallback((members: WorkspaceMember[]) => {
    // Don't auto-select any users on page load
    // Empty array means "show all transactions" (no filter)
    // Only set selectedUsers if explicitly provided in initialFilters (e.g., when navigating from Analytics)
    // But don't set it here - let the initialFilters sync effect handle it after transactions are loaded
    // This ensures we can validate users have transactions before setting them
  }, []);
  
  const workspaceData = useWorkspaceData({
    workspaceId,
    initialFilters,
    onMembersLoaded: handleMembersLoaded,
  });
  
  const {
    transactions,
    transactionsLoaded,
    hasMoreTransactions,
    lastTransactionDoc,
    allTransactionsRef,
    transactionsFromCache,
    refreshTransactions,
  } = useTransactions({
    workspaceId,
    membershipEnsured: workspaceData.membershipEnsured,
  });
  
  const { accounts, accountsLoaded } = useAccounts({ workspaceId });
  const { currencies, defaultCurrency, currenciesLoaded } = useCurrencies({ workspaceId });
  const { timeFormat, dateFormat, transactionIdPrefix, transactionIdNextSequence, allowExpenseFee, allowTransferFee } = useWorkspaceSettings({ workspaceId });
  
  // Local state
  // Initialize loading based on whether we have cached data from all hooks
  // If all hooks have cached data, start with loading=false for instant display
  const [loading, setLoading] = useState<boolean>(() => {
    // Check if all hooks indicate they have cached data
    // This runs during initial render, so hooks have already been called
    // We'll check this in an effect instead since hooks run during render
    return true; // Will be optimized in useEffect below
  });
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>(initialFilters?.typeFilter || "All");
  const [groupFilter, setGroupFilter] = useState<string>("All");
  const [categoryFilter, setCategoryFilter] = useState<string>(initialFilters?.category || "All");
  const [startDate, setStartDate] = useState<string>(initialFilters?.startDate || "");
  const [endDate, setEndDate] = useState<string>(initialFilters?.endDate || "");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionWithDate | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<"expense" | "income">("expense");
  const [editingTransaction, setEditingTransaction] = useState<TransactionWithDate | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  // Initialize selectedUsers as empty - will be set from initialFilters after validation
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedAccountFilters, setSelectedAccountFilters] = useState<string[]>(initialFilters?.account ? [initialFilters.account] : []);
  const [transferDialogOpen, setTransferDialogOpen] = useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [previewName, setPreviewName] = useState<string>("");
  const [previewType, setPreviewType] = useState<string | null>(null);
  const [previewExpiresAt, setPreviewExpiresAt] = useState<number | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState<boolean>(false);
  const [viewOnlyMode, setViewOnlyMode] = useState<boolean>(false);
  const [viewTransactionLoading, setViewTransactionLoading] = useState<boolean>(false);
  const openedViewForTransactionIdRef = useRef<string | null>(null);
  const { transactionHistory, loadingHistory, fetchTransactionHistory } = useTransactionHistory();
  const [showOnlyEdited, setShowOnlyEdited] = useState<boolean>(false);
  const { transactionHasHistory, setTransactionHasHistory, historyCheckComplete } = useTransactionHistoryCheck(workspaceId, transactions);
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(new Set());
  
  // Currency menu state
  const [currencyMenuAnchorEl, setCurrencyMenuAnchorEl] = useState<null | HTMLElement>(null);
  const currencyMenuOpen = Boolean(currencyMenuAnchorEl);
  
  // Pagination state
  const [isFullyLoaded, setIsFullyLoaded] = useState<boolean>(false);

  // Snackbar for demo mode alerts
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarElement, setSnackbarElement] = useState<{ severity: string; message: string } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Extract workspace data values
  const workspaceMembers = workspaceData.workspaceMembers;
  const membersLoaded = workspaceData.membersLoaded;
  const membershipEnsured = workspaceData.membershipEnsured;
  const permissions = workspaceData.permissions;
  const currentUserRole = workspaceData.currentUserRole;
  const workspaceOwnerId = workspaceData.workspaceOwnerId;
  const workspaceCreatedAt = workspaceData.workspaceCreatedAt;

  // Period filter (same logic as Analytics: Monthly default, arrows for navigation)
  const {
    selectedPeriod,
    currentPeriodOffset,
    handlePeriodChange,
    handlePeriodNavigation,
  } = useActivityPeriodFilter({
    workspaceId,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    workspaceCreatedAt,
    initialFilters,
  });

  // Track if filters have been manually changed by user (not from initial load)
  // Declared early so they can be used in initialFilters useEffect
  const typeFilterManuallyChanged = useRef<boolean>(false);
  const groupFilterManuallyChanged = useRef<boolean>(false);
  const categoryFilterManuallyChanged = useRef<boolean>(false);
  const userFilterInitialized = useRef<boolean>(false);
  
  // Animation state for empty state
  const [shouldAnimateEmptyState, setShouldAnimateEmptyState] = useState<boolean>(false);

  // Calendar helper functions moved to DateRangePicker component
  const hasAnimatedEmptyStateOnMountRef = useRef<boolean>(false);
  
  // Track location.pathname in a ref to avoid infinite loops
  const locationPathnameRef = useRef<string>(location.pathname);
  
  // Update ref when location changes (without triggering useEffect)
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);
  
  // Track if we've already processed redirect to prevent loops
  const hasProcessedRedirectRef = useRef<boolean>(false);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    title: "",
    category: "",
    amount: "",
    currency: "",
    rate: "",
    defaultCurrencyRate: "",
    date: new Date().toISOString().split("T")[0],
    time: "",
    accountId: "",
    accountGroup: "",
  });
  
  const [uploadingAttachment, setUploadingAttachment] = useState<boolean>(false);
  
  // Track if rate is manually edited or came from existing transaction
  const [rateManuallyEdited, setRateManuallyEdited] = useState<boolean>(false);
  const [rateFromTransaction, setRateFromTransaction] = useState<boolean>(false);
  
  // Track if default currency rate is manually edited
  const [defaultCurrencyRateManuallyEdited, setDefaultCurrencyRateManuallyEdited] = useState<boolean>(false);
  
  // Track if transfer rate is manually edited
  const [transferRateManuallyEdited, setTransferRateManuallyEdited] = useState<boolean>(false);
  
  // Redirect state - check if we need to redirect to transaction page
  const [shouldRedirect, setShouldRedirect] = useState<boolean>(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  // Transfer form state
  const [transferFormData, setTransferFormData] = useState<TransferFormData>({
    fromAccountId: "",
    toAccountId: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    time: "",
    transferRate: "",
  });
  
  const [uploadingTransferAttachment, setUploadingTransferAttachment] = useState<boolean>(false);
  
  // Time field visibility state
  const [showTime, setShowTime] = useState<boolean>(false);
  const [showTransferTime, setShowTransferTime] = useState<boolean>(false);
  const [showTransferFee, setShowTransferFee] = useState<boolean>(false);
  
  // Fee edit dialog state
  const [feeEditDialogOpen, setFeeEditDialogOpen] = useState<boolean>(false);
  const [feeEditFormData, setFeeEditFormData] = useState<{ feeAmount: string; feeAccountId: string }>({
    feeAmount: "",
    feeAccountId: "",
  });
  const [isSubmittingFee, setIsSubmittingFee] = useState<boolean>(false);
  
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false); // Closed by default on mobile, user can expand manually
  const [searchExpanded, setSearchExpanded] = useState<boolean>(false); // Search field collapsed by default on mobile
  
  // Calendar picker state for mobile (used by DateRangePicker)
  const [focusedDateField, setFocusedDateField] = useState<"startDate" | "endDate" | null>(null);
  
  // Pending card navigation (declarative Navigate works when imperative navigate fails in Brave/desktop mobile)
  const [pendingCardNav, setPendingCardNav] = useState<{ path: string; state: object } | null>(null);

  // Centered "Adjustment not editable" message (show then fade out after ~1s)
  const [showAdjustmentMessage, setShowAdjustmentMessage] = useState(false);
  const [adjustmentMessageVisible, setAdjustmentMessageVisible] = useState(false);
  const handleAdjustmentNotEditable = useCallback(() => {
    setShowAdjustmentMessage(true);
    setAdjustmentMessageVisible(true);
  }, []);
  useEffect(() => {
    if (!showAdjustmentMessage || !adjustmentMessageVisible) return;
    const t = setTimeout(() => setAdjustmentMessageVisible(false), 1000);
    return () => clearTimeout(t);
  }, [showAdjustmentMessage, adjustmentMessageVisible]);
  useEffect(() => {
    if (!showAdjustmentMessage || adjustmentMessageVisible) return;
    const t = setTimeout(() => setShowAdjustmentMessage(false), 350);
    return () => clearTimeout(t);
  }, [showAdjustmentMessage, adjustmentMessageVisible]);
  
  // Check if user was on transaction page and should auto-navigate back
  useEffect(() => {
    // Use ref to check pathname without causing infinite loops
    const currentPath = locationPathnameRef.current;
    
    // Only check when on activity page (not when already on create-transaction)
    if (!currentPath.includes('/activity') || currentPath.includes('/create-transaction')) {
      // Reset redirect state if we're not on activity page
      if (shouldRedirect) {
        setShouldRedirect(false);
        setRedirectPath(null);
      }
      // Reset redirect processing flag when leaving activity page
      hasProcessedRedirectRef.current = false;
      return;
    }
    
    // Prevent processing redirect multiple times
    if (hasProcessedRedirectRef.current) {
      return;
    }
    
    try {
      const routeStorageKey = `transaction-route-${workspaceId}`;
      const savedRoute = sessionStorage.getItem(routeStorageKey);
      
      if (savedRoute) {
        const routeInfo = JSON.parse(savedRoute);
        // Found saved transaction route, redirecting immediately
        if (workspaceSlug && routeInfo.type) {
          const targetPath = `/${workspaceSlug}/create-transaction/${routeInfo.type}`;
          // Clear the saved route immediately to prevent redirect loops
          sessionStorage.removeItem(routeStorageKey);
          // Mark as processed before navigating to prevent re-processing
          hasProcessedRedirectRef.current = true;
          setRedirectPath(targetPath);
          setShouldRedirect(true);
          // Navigate immediately with replace to avoid showing Activity
          navigate(targetPath, { replace: true });
          
          // Reset redirect state after a timeout to prevent getting stuck if navigation fails
          const timeoutId = setTimeout(() => {
            const currentPathAfterTimeout = locationPathnameRef.current;
            if (currentPathAfterTimeout.includes('/activity')) {
              // Still on activity page, navigation might have failed
              console.warn("⚠️ Activity: Redirect timeout, resetting redirect state");
              setShouldRedirect(false);
              setRedirectPath(null);
              hasProcessedRedirectRef.current = false;
            }
          }, 2000);
          
          return () => clearTimeout(timeoutId);
        } else {
          // Invalid route info, clear it
          sessionStorage.removeItem(routeStorageKey);
        }
      }
    } catch (error) {
      console.error("❌ Activity: Error checking saved transaction route:", error);
      // Clear invalid saved route on error
      if (workspaceId) {
        const routeStorageKey = `transaction-route-${workspaceId}`;
        sessionStorage.removeItem(routeStorageKey);
      }
    }
  }, [workspaceId, workspaceSlug, navigate, shouldRedirect]);

  // Set initial filter expansion state based on screen size
  useEffect(() => {
    // On desktop, expand filters by default; on mobile, keep them closed
    if (!isMobile) {
      setFiltersExpanded(true);
    } else {
      setFiltersExpanded(false);
    }
  }, [isMobile]);

  // Search input focus handling moved to TransactionSearch component

  // Track if form submission is in progress to prevent multiple submissions
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmittingTransfer, setIsSubmittingTransfer] = useState<boolean>(false);

  // Store initial form data when editing starts (for change detection)
  const [initialFormData, setInitialFormData] = useState<FormData | null>(null);
  const [initialTransferFormData, setInitialTransferFormData] = useState<TransferFormData | null>(null);

  // Categories state - fetched from API based on expense/income type
  const { expenseCategories, incomeCategories, categoryGroups } = useCategories({ workspaceId });
  const { fields: transactionCustomFields } = useTransactionCustomFieldsSettings({ workspaceId });
  const [selectedCategoryDetails, setSelectedCategoryDetails] = useState<TransactionCategoryDetail[]>([]);

  // Newly added detail options (temporary, not saved until transaction is created/updated)
  // Maps detail name to array of newly added options
  const [newlyAddedDetailOptions, setNewlyAddedDetailOptions] = useState<Record<string, string[]>>({});
  
  // Input values for list-type details (for freeSolo Autocomplete)
  const [detailInputValues, setDetailInputValues] = useState<Record<string, string>>({});
  
  // Track which detail dropdowns are open
  const [openDetailDropdowns, setOpenDetailDropdowns] = useState<Set<string>>(new Set());

  // Sync filters when initialFilters prop changes (e.g., when navigating from Analytics)
  // Don't mark as manually changed when setting from initialFilters
  useEffect(() => {
    if (initialFilters) {
      if (initialFilters.typeFilter) {
        typeFilterManuallyChanged.current = false; // Reset flag when setting from props
        setTypeFilter(initialFilters.typeFilter);
      }
      if (initialFilters.category) {
        categoryFilterManuallyChanged.current = false; // Reset flag when setting from props
        setCategoryFilter(initialFilters.category);
      }
      if (initialFilters.account) {
        setSelectedAccountFilters([initialFilters.account]);
      }
      if (initialFilters.startDate !== undefined) {
        setStartDate(initialFilters.startDate);
      }
      if (initialFilters.endDate !== undefined) {
        setEndDate(initialFilters.endDate);
      }
      if (initialFilters.users && initialFilters.users.length > 0) {
        // Only set users if we have transactions loaded to validate them
        // This prevents setting users without transactions which would cause flicker
        if (transactionsLoaded && membersLoaded) {
          if (transactions.length === 0) {
            // No transactions at all - keep empty (show all)
            // Don't set anything, selectedUsers is already empty
          } else {
            // Get set of user IDs who have created transactions
            const usersWithTransactions = new Set(
              transactions
                .map(t => t.userId)
                .filter((userId): userId is string => Boolean(userId))
            );
            // Filter out users without transactions (except bypass-user)
            const validUsers = initialFilters.users.filter(userId => 
              userId === "bypass-user" || usersWithTransactions.has(userId)
            );
            // Only set if we have valid users (don't set empty array here - that's the default)
            if (validUsers.length > 0) {
              setSelectedUsers(validUsers);
            }
            // If no valid users, keep selectedUsers as empty (default state)
          }
        }
        // If transactions/members not loaded yet, don't set anything
        // The auto-deselect effect will handle validation after data loads
      }
    }
  }, [initialFilters, transactionsLoaded, transactions]);

  // Auto-select group when category is set from initialFilters (e.g., from Analytics)
  useEffect(() => {
    if (!isFullyLoaded) return; // Don't auto-select until data is fully loaded
    if (initialFilters?.category && 
        categoryFilter === initialFilters.category && 
        groupFilter === "All") {
      const transactionCategory = expenseCategories.find((cat) => cat.name === categoryFilter) ||
                                  incomeCategories.find((cat) => cat.name === categoryFilter);
      if (transactionCategory && transactionCategory.groupId) {
        const categoryGroup = categoryGroups.find((g) => g.id === transactionCategory.groupId);
        if (categoryGroup) {
          // Auto-select the group when category is set from initialFilters
          setGroupFilter(categoryGroup.name);
        }
      }
    }
  }, [initialFilters, categoryFilter, groupFilter, expenseCategories, incomeCategories, categoryGroups, isFullyLoaded, setGroupFilter]);

  // Calendar view sync and click-outside handling moved to DateRangePicker component

  // Reset loading state when workspaceId changes (for proper empty state display on new workspace)
  useEffect(() => {
    if (workspaceId) {
      setLoading(true);
      // Reset animation state when workspace changes
      hasAnimatedEmptyStateOnMountRef.current = false;
      setShouldAnimateEmptyState(false);
      // Reset user filter initialization when workspace changes
      userFilterInitialized.current = false;
    }
  }, [workspaceId]);

  // Initialize animation state on mount (when navigating to Activity)
  useEffect(() => {
    hasAnimatedEmptyStateOnMountRef.current = false;
    setShouldAnimateEmptyState(false);
  }, []);

  // Trigger animation for empty state on mount/refresh when data is empty
  useEffect(() => {
    // Skip if still loading
    if (loading) return;

    // Check if this is initial mount with empty data
    const isInitialMountWithEmptyData = !hasAnimatedEmptyStateOnMountRef.current && transactions.length === 0;

    // Trigger animation if condition is met
    if (isInitialMountWithEmptyData) {
      // Add a delay to allow DOM to stabilize before animating
      // Use requestAnimationFrame to ensure the container is fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            // Enable animation for empty state
            setShouldAnimateEmptyState(true);
            
            // Mark as animated immediately for empty state
            setTimeout(() => {
              hasAnimatedEmptyStateOnMountRef.current = true;
            }, 50);
          }, 200); // Additional delay for container to calculate dimensions
        });
      });
    }
  }, [loading, transactions.length]);

  // When user manually edits rate, also update defaultCurrencyRate to match the rate
  // This handles cases where user wants custom conversion rates (e.g., rate = 2 means 1 expense = 2 default)
  useEffect(() => {
    // Only process for expense/income transactions (not transfers)
    if (!formData.accountId || !formData.currency) return;
    
    // Only update if rate was manually edited
    if (!rateManuallyEdited || !formData.rate) return;
    
    const account = accounts.find((acc) => acc.id === formData.accountId);
    if (!account) return;
    
    const accountCurrency = account.currency || defaultCurrency;
    const expenseCurrency = formData.currency || accountCurrency;
    
    const parsedRate = parseFloat(formData.rate);
    if (isNaN(parsedRate) || parsedRate <= 0) return;
    
    // When rate is manually edited and expense currency = account currency (currencies match),
    // update defaultCurrencyRate to match the rate
    // This ensures expenseToDefaultRate = rate (since exchangeRate = 1 when currencies match)
    const currenciesMatch = expenseCurrency === accountCurrency;
    
    if (currenciesMatch && accountCurrency !== defaultCurrency) {
      // Check if defaultCurrencyRate is not already equal to the rate
      const currentDefaultRate = formData.defaultCurrencyRate ? parseFloat(formData.defaultCurrencyRate) : null;
      if (currentDefaultRate === null || Math.abs(currentDefaultRate - parsedRate) > 0.0001) {
        // Update defaultCurrencyRate to match the manually edited rate
        setFormData((prev) => ({
          ...prev,
          defaultCurrencyRate: formatRate(parsedRate),
        }));
        // Mark as manually edited since user set rate manually
        setDefaultCurrencyRateManuallyEdited(true);
      }
    }
  }, [formData.rate, formData.defaultCurrencyRate, formData.accountId, formData.currency, rateManuallyEdited, accounts, defaultCurrency, setFormData, setDefaultCurrencyRateManuallyEdited]);

  // Transactions fetching is now handled by useTransactions hook

  // Membership ensuring, permissions, and members fetching are now handled by useWorkspaceData hook

  // Members, accounts, currencies, and categories fetching are now handled by their respective hooks

  // Category details logic moved to useCategoryDetails hook

  // Format currency, rate, time functions - now imported from utils

  // Get account name by ID
  // Account helper functions moved to useAccountHelpers hook
  const { getAccountName, getAccountDisplayName, getAccountGroup, getAccountById } = useAccountHelpers(accounts);

  // Pagination logic moved to usePagination hook
  // Note: filteredTransactionsLength will be updated after filtering
  const paginationResult = usePagination({
    workspaceId,
    transactions,
    hasMoreTransactions,
    lastTransactionDoc,
    isFullyLoaded,
    tableContainerRef,
    allTransactionsRef,
    // filteredTransactionsLength will use allTransactions.length as fallback
  });

  const { allTransactions, displayedCount, setDisplayedCount, loadingMore, hasMoreToLoad } = paginationResult;

  // Activity helper functions (needed for cache before allWorkspaceMembers)
  const {
    formatDate,
    formatTime,
    getCategoryIcon,
    getAttachmentIcon,
    getUserName,
    getUserNameForHistory,
    getUserPhotoURL,
    userNameCache,
    userPhotoURLCache,
  } = useActivityHelpers({ workspaceMembers, transactions, workspaceOwnerId, timeFormat, dateFormat });
  
  // In bypass mode, dynamically add users from transactions to workspaceMembers
  // This ensures users who created transactions appear in the filter list
  const [additionalMembers, setAdditionalMembers] = useState<WorkspaceMember[]>([]);
  useEffect(() => {
    if (!isBypassAuthEnabled() || transactions.length === 0) {
      setAdditionalMembers([]);
      return;
    }
    
    // Get all unique user IDs from transactions
    const transactionUserIds = new Set(
      transactions
        .map(t => t.userId)
        .filter((userId): userId is string => Boolean(userId))
    );
    
    // Find users in transactions that aren't in workspaceMembers
    const workspaceMemberIds = new Set(workspaceMembers.map(u => u.id));
    const userIdsToAdd = Array.from(transactionUserIds).filter(
      userId => 
        !workspaceMemberIds.has(userId) &&
        userId !== "bypass-user" &&
        userId !== "demo-user"
    );
    
    if (userIdsToAdd.length === 0) {
      setAdditionalMembers([]);
      return;
    }
    
    // Create WorkspaceMember objects for users we have cached names for
    const newMembers: WorkspaceMember[] = [];
    userIdsToAdd.forEach(userId => {
      const cachedName = userNameCache[userId];
      const cachedPhotoURL = userPhotoURLCache[userId];
      
      // Only add if we have a cached name (user-friendly)
      // If we don't have a name yet, the cache fetch is still in progress
      if (cachedName) {
        newMembers.push({
          id: userId,
          name: cachedName,
          email: undefined, // We don't have email from cache, only name
          role: undefined, // Not a workspace member, just a transaction creator
          photoURL: cachedPhotoURL,
        });
      }
    });
    
    setAdditionalMembers(newMembers);
    
    if (process.env.NODE_ENV === "development" && newMembers.length > 0) {
      console.log(`[Activity] Added ${newMembers.length} users from transactions to filter list:`, newMembers.map(m => m.id));
    }
  }, [transactions, workspaceMembers, userNameCache, userPhotoURLCache]);
  
  // Merge additional members with workspaceMembers for filter display
  // This must be declared before useTransactionFilters
  const allWorkspaceMembers = useMemo(() => {
    const merged = [...workspaceMembers];
    additionalMembers.forEach(additionalMember => {
      if (!merged.find(m => m.id === additionalMember.id)) {
        merged.push(additionalMember);
      }
    });
    return merged;
  }, [workspaceMembers, additionalMembers]);

  // Use custom hook for transaction filtering
  const {
    filteredTransactions,
    baseFilteredTransactions,
    availableTypes,
    availableGroups,
    availableCategories,
  } = useTransactionFilters({
    transactions: allTransactions,
    searchQuery,
    typeFilter,
    groupFilter,
    categoryFilter,
    startDate,
    endDate,
    selectedUsers,
    selectedAccountFilters,
    showOnlyEdited,
    transactionIdPrefix,
    accounts,
    categoryGroups,
    expenseCategories,
    incomeCategories,
    transactionHasHistory,
    workspaceMembers: allWorkspaceMembers,
    workspaceOwnerId: workspaceData.workspaceOwnerId,
    isDemoWorkspace: workspaceId === getDemoWorkspaceId(),
    getAccountName,
  });

  // Auto-filter selection effects moved to useAutoFilterSelection hook
  const {
    handleTypeFilterChange,
    handleGroupFilterChange,
    handleCategoryFilterChange,
  } = useAutoFilterSelection({
    typeFilter,
    groupFilter,
    categoryFilter,
    transactions,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    isFullyLoaded,
    setTypeFilter,
    setGroupFilter,
    setCategoryFilter,
    typeFilterManuallyChanged,
    groupFilterManuallyChanged,
    categoryFilterManuallyChanged,
  });

  // Filter reset effects moved to useFilterReset hook
  useFilterReset({
    groupFilter,
    categoryFilter,
    categoryGroups,
    expenseCategories,
    incomeCategories,
    setGroupFilter,
    setCategoryFilter,
  });

  // Check if everything is fully loaded (including history check so edited icons and avatars show with the list)
  // When re-entering from cache, show list immediately; edit icon space is reserved in TransactionList to avoid layout shift
  useEffect(() => {
    const readyFromCache = transactionsFromCache && transactionsLoaded && accountsLoaded && currenciesLoaded && membersLoaded;
    const readyFromFresh = transactionsLoaded && accountsLoaded && currenciesLoaded && membersLoaded && historyCheckComplete;
    if (readyFromCache || readyFromFresh) {
      setLoading(false);
      const timeoutId = setTimeout(() => {
        setIsFullyLoaded(true);
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      const hasAnyDataLoaded = transactionsLoaded || accountsLoaded || currenciesLoaded || membersLoaded;
      if (hasAnyDataLoaded) setLoading(true);
    }
  }, [transactionsLoaded, accountsLoaded, currenciesLoaded, membersLoaded, historyCheckComplete, transactionsFromCache]);

  // Fallback safeguard: If transactions are loaded but loading is still true after 5 seconds,
  // force loading to false to prevent the page from being stuck
  useEffect(() => {
    if (transactionsLoaded && loading) {
      const fallbackTimeoutId = setTimeout(() => {
        if (loading) {
          console.warn("Activity page: Loading timeout reached, forcing loading state to false");
          setLoading(false);
          setIsFullyLoaded(true);
        }
      }, 5000); // 5 second fallback
      
      return () => {
        clearTimeout(fallbackTimeoutId);
      };
    }
  }, [transactionsLoaded, loading]);

  // Note: User filter now works like account filter - simple selection, no transaction checks

  // Reset displayed count when filters change (but not when just toggling showOnlyEdited if we have history data)
  useEffect(() => {
    // Only reset if transactionHasHistory is already populated (to avoid loading flicker)
    if (transactionHasHistory.size > 0 || !showOnlyEdited) {
      setDisplayedCount(20);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, typeFilter, categoryFilter, startDate, endDate, selectedUsers, showOnlyEdited]);

  // When user searches by transaction ID (e.g. DV-1), auto-switch period to "All time"
  useEffect(() => {
    if (isSearchByTransactionId(searchQuery, transactionIdPrefix) && selectedPeriod !== "All time") {
      handlePeriodChange("All time");
    }
  }, [searchQuery, transactionIdPrefix, selectedPeriod, handlePeriodChange]);

  // Separate effect for showOnlyEdited to avoid unnecessary resets
  useEffect(() => {
    if (transactionHasHistory.size > 0) {
      setDisplayedCount(20);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyEdited]);

  // Pagination logic moved to usePagination hook

  // handleWheel moved to useMenuHandlers hook

  // Permission helpers moved to usePermissionHelpers hook
  const { canDeleteTransactions, canEditTransactions, canViewTransactionHistory } = usePermissionHelpers({
    permissions,
    currentUserRole,
    workspaceOwnerId,
  });

  // Action button handlers moved to useActionButtonHandlers hook
  const {
    handleExpenseClick,
    handleIncomeClick,
    handleTransferClick,
  } = useActionButtonHandlers({
    setDialogType,
    setEditingTransaction,
    setFormData,
    setTransferFormData,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setSelectedCategoryDetails,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
    setOpenDetailDropdowns,
    setRateManuallyEdited,
    setTransferRateManuallyEdited,
    setDialogOpen,
    setTransferDialogOpen,
  });

  // Menu handlers moved to useMenuHandlers hook

  // Get currency rate
  // Currency helper functions moved to useCurrencyHelpers hook
  const { getCurrencyRate, calculateTransferRate } = useCurrencyHelpers(currencies, defaultCurrency);

  // Temporary handleMenuClose for useTransactionMenuHandlers (will be overridden by useMenuHandlers)
  const tempHandleMenuClose = useCallback((): void => {
    setMenuAnchor(null);
    setSelectedTransaction(null);
  }, [setMenuAnchor, setSelectedTransaction]);

  // Transaction menu handlers moved to useTransactionMenuHandlers hook
  const {
    handleEdit,
    handleDuplicate,
    handleDelete,
    handleHistory,
    handleCopyUrl,
    handleDeleteConfirm,
    isProtectedAdjustment,
    usesArchivedAccount,
    getArchivedAccountNames,
    getAccountBalanceChanges,
  } = useTransactionMenuHandlers({
    selectedTransaction,
    workspaceId,
    workspaceSlug,
    isMobile,
    navigate,
    accounts,
    defaultCurrency,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    getAccountById,
    setEditingTransaction,
    setDialogOpen,
    setTransferDialogOpen,
    setDialogType,
    setFormData,
    setInitialFormData,
    setTransferFormData,
    setInitialTransferFormData,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setSelectedCategoryDetails,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    calculateTransferRate,
    setMenuAnchor,
    setDeleteConfirmOpen,
    setSelectedTransaction,
    setHistoryDialogOpen,
    setSnackbarElement,
    setSnackbarOpen,
    transactionCustomFields,
    handleMenuClose: tempHandleMenuClose,
    fetchTransactionHistory,
  });

  // Open edit dialog in view-only mode when URL has transaction id (shared link).
  // Start fetch immediately (don't wait for transactions list) and show loading overlay until ready.
  useEffect(() => {
    if (!initialViewTransactionId || !workspaceId) return;
    const id = initialViewTransactionId;
    if (openedViewForTransactionIdRef.current === id) return;
    const fromList = allTransactionsRef.current.find((t) => t.id === id) ?? transactions.find((t) => t.id === id);
    if (fromList) {
      openedViewForTransactionIdRef.current = id;
      handleEdit(fromList);
      setViewOnlyMode(true);
      return;
    }
    setViewTransactionLoading(true);
    let cancelled = false;
    const fetchOne = async (): Promise<void> => {
      try {
        const response = await api.makeAuthenticatedRequest(
          `/workspaces/${workspaceId}/transactions/${id}`,
          { method: "GET" }
        );
        if (cancelled || !response.ok) return;
        const data = await response.json();
        let dateValue = parseTransactionDateFromRaw(data.date);
        // If API returns date and time separately, combine so the dialog shows both
        if (data.time && typeof data.time === "string" && data.time.trim()) {
          const dateOnly = dateValue.getFullYear() + "-" + String(dateValue.getMonth() + 1).padStart(2, "0") + "-" + String(dateValue.getDate()).padStart(2, "0");
          const combined = combineDateAndTime(dateOnly, data.time.trim());
          dateValue = new Date(combined);
        }
        const tx: TransactionWithDate = {
          ...data,
          date: dateValue,
        };
        if (!cancelled) {
          openedViewForTransactionIdRef.current = id;
          handleEdit(tx);
          setViewOnlyMode(true);
        }
      } catch {
        if (!cancelled) {
          openedViewForTransactionIdRef.current = null;
        }
      } finally {
        if (!cancelled) {
          setViewTransactionLoading(false);
        }
      }
    };
    fetchOne();
    return () => {
      cancelled = true;
      setViewTransactionLoading(false);
    };
  }, [initialViewTransactionId, workspaceId, handleEdit]);

  // Menu handlers (open/close/wheel) moved to useMenuHandlers hook
  const {
    handleMenuOpen,
    handleMenuClose,
    handleWheel,
  } = useMenuHandlers({
    isMobile,
    isProtectedAdjustment,
    tableContainerRef,
    setMenuAnchor,
    setSelectedTransaction,
  });

  // Mobile: open transaction edit page when user taps a transaction card.
  // Pass transaction in state so edit screen can show immediately without waiting for API (fixes ~5s delay after hard refresh).
  const handleTransactionCardClick = useCallback(
    (transaction: TransactionWithDate) => {
      if (!workspaceSlug) return;
      if (isProtectedAdjustment(transaction) || usesArchivedAccount(transaction)) return;
      const transactionType =
        transaction.type === "transfer"
          ? "transfer"
          : transaction.isIncome
            ? "income"
            : "expense";
      const path = `/${workspaceSlug}/edit-transaction/${transactionType}/${transaction.id}`;
      const state = { transaction };
      // Defer to next tick so navigation runs after touch event.
      // Use declarative Navigate (setState) instead of imperative navigate() - fixes URL changing
      // but view not rendering in Brave/desktop mobile mode.
      setTimeout(() => {
        setPendingCardNav({ path, state });
      }, 0);
    },
    [workspaceSlug, isProtectedAdjustment, usesArchivedAccount]
  );

  // Transaction form handlers moved to useTransactionFormHandlers hook
  const {
    handleAddClick,
    handleAccountChange,
    handleRefreshRate,
    handleRefreshDefaultCurrencyRate,
    handleRefreshTime,
    handleRefreshTransferRate,
    handleRefreshTransferTime,
    getConvertedAmount,
  } = useTransactionFormHandlers({
    formData,
    transferFormData,
    defaultCurrency,
    accounts,
    isMobile,
    workspaceSlug,
    setFormData,
    setTransferFormData,
    setEditingTransaction,
    setInitialFormData,
    setInitialTransferFormData,
    setDialogOpen,
    setTransferDialogOpen,
    setDialogType,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    setTransferRateManuallyEdited,
    getAccountById,
    calculateTransferRate,
  });

  // Memoize delete warning message to recalculate when selectedTransaction or accounts change
  // Note: We include accounts.length to ensure recalculation when accounts array changes
  const deleteWarningMessage = useMemo(() => {
    // Only calculate when transaction is selected
    if (!selectedTransaction) {
      return null;
    }
    
    // Calculate balance changes directly here to ensure we use the latest accounts
    const balanceChanges = getAccountBalanceChanges(selectedTransaction);
    
    // Always try to show warning message - use balanceChanges if available, otherwise calculate directly
    if (balanceChanges.length > 0) {
      // Use the calculated balance changes
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
            Account balances will be updated:
          </Typography>
          {balanceChanges.map((change) => (
            <Typography key={change.accountId} variant="body2" sx={{ mb: 0.5, pl: 1 }}>
              <strong>{change.accountName}</strong>: {formatCurrency(change.currentBalance, change.currency)} → {formatCurrency(change.newBalance, change.currency)}
            </Typography>
          ))}
        </Box>
      );
    }
    
    // Fallback: Calculate directly from transaction data
    // For expense/income transactions
    if (selectedTransaction.type === "expense" || selectedTransaction.type === "income") {
      const accountName = selectedTransaction.accountName || "Unknown Account";
      const amount = selectedTransaction.amount || 0;
      const currency = selectedTransaction.currency || defaultCurrency;
      const isIncome = selectedTransaction.isIncome !== undefined ? selectedTransaction.isIncome : selectedTransaction.type === "income";
      
      // Try to find account to get current balance
      const account = selectedTransaction.accountId ? getAccountById(selectedTransaction.accountId) : null;
      const currentBalance = account?.amount ?? 0;
      const accountCurrency = account?.currency || currency;
      const newBalance = isIncome 
        ? currentBalance - amount 
        : currentBalance + amount;
      
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
            Account balance will be updated:
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>
            <strong>{accountName}</strong>: {formatCurrency(currentBalance, accountCurrency)} → {formatCurrency(newBalance, accountCurrency)}
          </Typography>
        </Box>
      );
    }
    
    // For transfers
    if (selectedTransaction.type === "transfer") {
      const fromAccountName = selectedTransaction.fromAccountName || "Unknown Account";
      const toAccountName = selectedTransaction.toAccountName || "Unknown Account";
      const amount = selectedTransaction.amount || 0;
      const transferRate = selectedTransaction.transferRate;
      
      const fromAccount = selectedTransaction.fromAccountId ? getAccountById(selectedTransaction.fromAccountId) : null;
      const toAccount = selectedTransaction.toAccountId ? getAccountById(selectedTransaction.toAccountId) : null;
      
      const fromCurrentBalance = fromAccount?.amount ?? 0;
      const fromCurrency = fromAccount?.currency || defaultCurrency;
      const fromNewBalance = fromCurrentBalance + amount;
      
      const toAmount = transferRate ? amount * transferRate : amount;
      const toCurrentBalance = toAccount?.amount ?? 0;
      const toCurrency = toAccount?.currency || defaultCurrency;
      const toNewBalance = toCurrentBalance - toAmount;
      
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
            Account balances will be updated:
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>
            <strong>{fromAccountName}</strong>: {formatCurrency(fromCurrentBalance, fromCurrency)} → {formatCurrency(fromNewBalance, fromCurrency)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, pl: 1 }}>
            <strong>{toAccountName}</strong>: {formatCurrency(toCurrentBalance, toCurrency)} → {formatCurrency(toNewBalance, toCurrency)}
          </Typography>
        </Box>
      );
    }
    
    return null;
  }, [selectedTransaction, accounts, accounts.length, getAccountBalanceChanges, getAccountById, defaultCurrency]);

  // Form validation moved to useFormValidation hook
  const {
    isTransactionFormValid,
    isTransferFormValid,
    hasTransactionChanges,
    hasTransferChanges,
  } = useFormValidation({
    formData,
    transferFormData,
    editingTransaction,
    initialFormData,
    initialTransferFormData,
    accounts,
    defaultCurrency,
    currencies,
  });

  // Filter helpers moved to useFilterHelpers hook
  const {
    hasActiveFilters,
    clearAllFilters,
    clearTypeFilter,
    clearGroupFilter,
    clearCategoryFilter,
    clearStartDate,
    clearEndDate,
    clearAccountFilter,
    clearUserFilter,
    clearEditedFilter,
  } = useFilterHelpers({
    searchQuery,
    typeFilter,
    groupFilter,
    categoryFilter,
    startDate,
    endDate,
    selectedUsers,
    selectedAccountFilters,
    showOnlyEdited,
    workspaceMembers,
    accounts,
    setSearchQuery,
    setTypeFilter,
    setGroupFilter,
    setCategoryFilter,
    setStartDate,
    setEndDate,
    setSelectedAccountFilters,
    setSelectedUsers,
    setShowOnlyEdited,
    onClearDatesToDefault: () => handlePeriodChange("Monthly"),
    selectedPeriod,
    currentPeriodOffset,
  });

  // Dialog close handlers moved to useDialogHandlers hook
  const {
    handleDialogClose,
    handleTransferDialogClose,
  } = useDialogHandlers({
    defaultCurrency,
    setDialogOpen,
    setTransferDialogOpen,
    setEditingTransaction,
    setInitialFormData,
    setInitialTransferFormData,
    setIsSubmitting,
    setIsSubmittingTransfer,
    setCurrencyMenuAnchorEl,
    setFormData,
    setTransferFormData,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setRateFromTransaction,
    setRateManuallyEdited,
    setTransferRateManuallyEdited,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
  });

  // Category details logic moved to useCategoryDetails hook
  const {
    deriveCategoryDetails,
    handleCreateDetailOption,
  } = useCategoryDetails({
    dialogType,
    dialogOpen,
    transferDialogOpen,
    formData,
    editingTransaction,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions: allTransactions,
    selectedCategoryDetails,
    newlyAddedDetailOptions,
    detailInputValues,
    setSelectedCategoryDetails,
    setFormData,
    setDetailInputValues,
    setNewlyAddedDetailOptions,
    setOpenDetailDropdowns,
  });

  // Get fee transaction for fee dialog (transfer fee or expense fee)
  const feeTransaction = editingTransaction &&
    editingTransaction.type === "expense" &&
    ((editingTransaction.title === "Transfer Fee" || editingTransaction.category === "Transfer Fee") && editingTransaction.transferFeeFor ||
     (editingTransaction.title === "Expense Fee" || editingTransaction.category === "Expense Fee") && (editingTransaction as any).expenseFeeFor)
    ? editingTransaction
    : null;

  // Transaction submission handlers moved to useTransactionSubmission hook
  // Must be called before useFeeSubmission since it provides saveTransactionHistory
  const {
    handleSubmit,
    handleTransferSubmit,
    saveTransactionHistory,
    saveCreationHistory,
  } = useTransactionSubmission({
    workspaceId,
    workspaceOwnerId,
    formData,
    transferFormData,
    editingTransaction,
    dialogType,
    defaultCurrency,
    accounts,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    newlyAddedDetailOptions,
    isSubmitting,
    isSubmittingTransfer,
    setIsSubmitting,
    setIsSubmittingTransfer,
    setDialogOpen,
    setTransferDialogOpen,
    setEditingTransaction,
    setFormData,
    setTransferFormData,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setUploadingAttachment,
    setUploadingTransferAttachment,
    setSnackbarElement,
    setSnackbarOpen,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
    setTransactionHasHistory,
    getAccountById,
    getAccountName,
    calculateTransferRate,
    defaultCurrencyRateManuallyEdited,
    transactionCustomFields,
    allowExpenseFee,
    allowTransferFee,
    onTransactionSaved: refreshTransactions,
  });

  // Fee submission handler - must be called after useTransactionSubmission
  const { handleFeeSubmit } = useFeeSubmission({
    workspaceId,
    feeTransaction: feeTransaction || ({} as TransactionWithDate),
    feeFormData: feeEditFormData,
    accounts,
    defaultCurrency,
    isSubmittingFee,
    setIsSubmittingFee,
    setFeeEditDialogOpen,
    setEditingTransaction,
    setTransferFormData,
    setShowTransferFee,
    setFormData,
    setSnackbarElement,
    setSnackbarOpen,
    saveTransactionHistory, // Now available from useTransactionSubmission above
    setTransactionHasHistory,
    getAccountName,
  });

  // Detect fee transactions and open fee edit dialog (transfer fee or expense fee)
  useEffect(() => {
    if (editingTransaction && !dialogOpen && !transferDialogOpen && !feeEditDialogOpen) {
      const isTransferFee = editingTransaction.type === "expense" &&
        (editingTransaction.title === "Transfer Fee" || editingTransaction.category === "Transfer Fee") &&
        editingTransaction.transferFeeFor;
      const isExpenseFee = editingTransaction.type === "expense" &&
        (editingTransaction.title === "Expense Fee" || editingTransaction.category === "Expense Fee") &&
        (editingTransaction as any).expenseFeeFor;
      const isFeeTransaction = isTransferFee || isExpenseFee;

      if (isFeeTransaction) {
        setFeeEditFormData({
          feeAmount: editingTransaction.amount?.toString() || "",
          feeAccountId: editingTransaction.accountId || "",
        });
        setFeeEditDialogOpen(true);
      }
    }
  }, [editingTransaction, dialogOpen, transferDialogOpen, feeEditDialogOpen]);

  // Rate auto-update effects moved to useRateAutoUpdate hook
  useRateAutoUpdate({
    formData,
    transferFormData,
    accounts,
    defaultCurrency,
    editingTransaction,
    initialFormData,
    initialTransferFormData,
    rateFromTransaction,
    rateManuallyEdited,
    defaultCurrencyRateManuallyEdited,
    calculateTransferRate,
    setFormData,
    setTransferFormData,
    setInitialFormData,
    setInitialTransferFormData,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
  });

  // Form handlers moved to useTransactionFormHandlers hook


  // handleCreateDetailOption moved to useCategoryDetails hook

  // Format date, formatTime, getCategoryIcon, getAttachmentIcon moved to useActivityHelpers hook

  // Dialog close handlers moved to useDialogHandlers hook

  // User and account filter handlers moved to FilterButtons component

  // Form validation functions moved to useFormValidation hook

  // Filter helper functions moved to useFilterHelpers hook

  // getUserName and getUserNameForHistory moved to useActivityHelpers hook

  // saveCreationHistory and saveTransactionHistory moved to useTransactionSubmission hook

  // handleHistory moved to useTransactionMenuHandlers hook

  // Transaction history checking logic moved to useTransactionHistoryCheck hook

  // If we should redirect, show loading state while redirecting
  // But only if we're actually on the activity page (not if navigation already happened)
  if (shouldRedirect && redirectPath && location.pathname.includes('/activity')) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pendingCardNav) {
    return <Navigate to={pendingCardNav.path} state={pendingCardNav.state} replace />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", bgcolor: "background.default" }}>
      {/* Loading overlay when opening transaction from shared URL */}
      <Backdrop
        open={viewTransactionLoading}
        sx={{ zIndex: 1300, flexDirection: "column", gap: 2 }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body1" color="inherit">Loading transaction…</Typography>
      </Backdrop>
      {/* Filters */}
      <TransactionFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchExpanded={searchExpanded}
        onSearchExpandedChange={setSearchExpanded}
        typeFilter={typeFilter}
        groupFilter={groupFilter}
        categoryFilter={categoryFilter}
        selectedPeriod={selectedPeriod}
        startDate={startDate}
        endDate={endDate}
        selectedUsers={selectedUsers}
        selectedAccountFilters={selectedAccountFilters}
        showOnlyEdited={showOnlyEdited}
        onTypeFilterChange={handleTypeFilterChange}
        onGroupFilterChange={handleGroupFilterChange}
        onCategoryFilterChange={handleCategoryFilterChange}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onPeriodChange={handlePeriodChange}
        onPeriodNavigation={handlePeriodNavigation}
        onUsersChange={setSelectedUsers}
        onAccountFiltersChange={setSelectedAccountFilters}
        onShowOnlyEditedChange={setShowOnlyEdited}
        onClearAllFilters={clearAllFilters}
        focusedDateField={focusedDateField}
        onFocusedDateFieldChange={setFocusedDateField}
        availableTypes={availableTypes}
        availableGroups={availableGroups}
        availableCategories={availableCategories}
        accounts={accounts}
        workspaceMembers={allWorkspaceMembers}
        transactions={transactions}
        getAccountDisplayName={getAccountDisplayName}
        getUserName={getUserName}
        filtersExpanded={filtersExpanded}
        onFiltersExpandedChange={setFiltersExpanded}
        filteredCount={hasActiveFilters ? filteredTransactions.length : undefined}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Transactions List */}
      <TransactionList
        loading={loading}
        transactions={transactions}
        filteredTransactions={filteredTransactions}
        displayedCount={displayedCount}
        hasMoreTransactions={hasMoreToLoad}
        loadingMore={loadingMore}
        shouldAnimateEmptyState={shouldAnimateEmptyState}
        expandedDetails={expandedDetails}
        transactionHasHistory={transactionHasHistory}
        workspaceMembers={allWorkspaceMembers}
        accounts={accounts}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        isMobile={isMobile}
        hasActiveFilters={hasActiveFilters}
        expenseCategories={expenseCategories}
        incomeCategories={incomeCategories}
        categoryGroups={categoryGroups}
        transactionCustomFields={transactionCustomFields}
        transactionIdPrefix={transactionIdPrefix}
        workspaceId={workspaceId}
        onAttachmentError={(msg) => {
          setSnackbarElement({ severity: "error", message: msg });
          setSnackbarOpen(true);
        }}
        tableContainerRef={tableContainerRef}
        handleMenuOpen={handleMenuOpen}
        onTransactionClick={handleTransactionCardClick}
        onAdjustmentNotEditable={handleAdjustmentNotEditable}
        setPreviewOpen={setPreviewOpen}
        setPreviewUrl={setPreviewUrl}
        setPreviewName={setPreviewName}
        setPreviewType={setPreviewType}
        setPreviewExpiresAt={setPreviewExpiresAt}
        setExpandedDetails={setExpandedDetails}
        clearAllFilters={clearAllFilters}
        handleWheel={handleWheel}
        getAccountName={getAccountName}
        getAccountById={getAccountById}
        getAccountGroup={getAccountGroup}
        getUserName={getUserName}
        getUserPhotoURL={getUserPhotoURL}
        getCategoryIcon={getCategoryIcon}
        formatDate={formatDate}
        formatTime={formatTime}
        getAttachmentIcon={getAttachmentIcon}
        isProtectedAdjustment={isProtectedAdjustment}
        usesArchivedAccount={usesArchivedAccount}
        getArchivedAccountNames={getArchivedAccountNames}
        calculateTransferRate={calculateTransferRate}
      />

      {/* Transaction Menu */}
      <TransactionMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        selectedTransaction={selectedTransaction}
        canViewTransactionHistory={canViewTransactionHistory}
        canDeleteTransactions={canDeleteTransactions}
        canEditTransactions={canEditTransactions}
        isProtectedAdjustment={isProtectedAdjustment}
        usesArchivedAccount={usesArchivedAccount}
        getArchivedAccountNames={getArchivedAccountNames}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onCopyUrl={handleCopyUrl}
        onHistory={handleHistory}
        onDelete={handleDelete}
      />

      {/* Centered "Adjustment not editable" message (fades out after ~1s) */}
      {showAdjustmentMessage && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1300}
          sx={{
            pointerEvents: "none",
            opacity: adjustmentMessageVisible ? 1 : 0,
            transition: "opacity 0.3s ease-out",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              bgcolor: "background.paper",
              color: "text.primary",
              px: 2,
              py: 1.5,
              borderRadius: 1,
              boxShadow: 2,
            }}
          >
            Adjustment not editable
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmOpen && selectedTransaction && (
        <DeleteConfirmationDialog
          key={`delete-${selectedTransaction.id}`}
          open={true}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setSelectedTransaction(null);
          }}
          onConfirm={handleDeleteConfirm}
          itemName={selectedTransaction.title || ""}
          itemType="transaction"
          warningMessage={deleteWarningMessage}
          showWarningAsAlert={true}
        />
      )}

      {/* Transaction History Dialog */}
      <TransactionHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        transactionHistory={transactionHistory}
        loadingHistory={loadingHistory}
        selectedTransaction={selectedTransaction}
        workspaceId={workspaceId}
        transactionIdPrefix={transactionIdPrefix ?? ""}
        getUserNameForHistory={getUserNameForHistory}
        getUserName={getUserName}
        getAttachmentIcon={getAttachmentIcon}
        formatDate={formatDate}
        formatTime={formatTime}
        transactionCustomFields={transactionCustomFields}
        onAttachmentError={(msg) => {
          setSnackbarElement({ severity: "error", message: msg });
          setSnackbarOpen(true);
        }}
      />

      {/* Fee Edit Dialog */}
      {feeTransaction && (
        <FeeEditDialog
          open={feeEditDialogOpen}
          onClose={() => {
            setFeeEditDialogOpen(false);
            setEditingTransaction(null);
          }}
          onSubmit={handleFeeSubmit}
          formData={feeEditFormData}
          setFormData={setFeeEditFormData}
          feeTransaction={feeTransaction}
          transferTransaction={feeTransaction.transferFeeFor ? (allTransactions.find(t => t.id === feeTransaction.transferFeeFor) || null) : null}
          expenseTransaction={(feeTransaction as any).expenseFeeFor ? (allTransactions.find(t => t.id === (feeTransaction as any).expenseFeeFor) || null) : null}
          transactionIdPrefix={transactionIdPrefix ?? ""}
          accounts={accounts}
          currencies={currencies}
          defaultCurrency={defaultCurrency}
          isSubmitting={isSubmittingFee}
          isValid={!!(feeEditFormData.feeAmount && parseFloat(feeEditFormData.feeAmount) >= 0 && feeEditFormData.feeAccountId)}
          hasFeeChanges={
            feeEditFormData.feeAmount !== (feeTransaction.amount?.toString() ?? "") ||
            feeEditFormData.feeAccountId !== (feeTransaction.accountId ?? "")
          }
        />
      )}

      {/* Transaction Dialog Component */}
      <TransactionDialog
        workspaceId={workspaceId}
        dialogOpen={dialogOpen}
        transferDialogOpen={transferDialogOpen}
        dialogType={dialogType}
        editingTransaction={editingTransaction}
        isSubmitting={isSubmitting}
        isSubmittingTransfer={isSubmittingTransfer}
        formData={formData}
        transferFormData={transferFormData}
        showTime={showTime}
        showTransferTime={showTransferTime}
        currencyMenuAnchorEl={currencyMenuAnchorEl}
        uploadingAttachment={uploadingAttachment}
        uploadingTransferAttachment={uploadingTransferAttachment}
        selectedCategoryDetails={selectedCategoryDetails}
        newlyAddedDetailOptions={newlyAddedDetailOptions}
        detailInputValues={detailInputValues}
        openDetailDropdowns={openDetailDropdowns}
        rateManuallyEdited={rateManuallyEdited}
        defaultCurrencyRateManuallyEdited={defaultCurrencyRateManuallyEdited}
        transferRateManuallyEdited={transferRateManuallyEdited}
        accounts={accounts}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        expenseCategories={expenseCategories}
        incomeCategories={incomeCategories}
        categoryGroups={categoryGroups}
        allowExpenseFee={allowExpenseFee}
        allowTransferFee={allowTransferFee}
        setDialogOpen={setDialogOpen}
        setTransferDialogOpen={setTransferDialogOpen}
        setDialogType={setDialogType}
        setEditingTransaction={setEditingTransaction}
        setIsSubmitting={setIsSubmitting}
        setIsSubmittingTransfer={setIsSubmittingTransfer}
        setFormData={setFormData}
        setTransferFormData={setTransferFormData}
        setShowTime={setShowTime}
        setShowTransferTime={setShowTransferTime}
        setShowTransferFee={setShowTransferFee}
        setCurrencyMenuAnchorEl={setCurrencyMenuAnchorEl}
        setUploadingAttachment={setUploadingAttachment}
        setUploadingTransferAttachment={setUploadingTransferAttachment}
        setSelectedCategoryDetails={setSelectedCategoryDetails}
        setNewlyAddedDetailOptions={setNewlyAddedDetailOptions}
        setDetailInputValues={setDetailInputValues}
        setOpenDetailDropdowns={setOpenDetailDropdowns}
        setRateManuallyEdited={setRateManuallyEdited}
        setDefaultCurrencyRateManuallyEdited={setDefaultCurrencyRateManuallyEdited}
        setTransferRateManuallyEdited={setTransferRateManuallyEdited}
        handleSubmit={handleSubmit}
        handleTransferSubmit={handleTransferSubmit}
        handleDialogClose={() => {
          handleDialogClose();
          setViewOnlyMode(false);
        }}
        handleTransferDialogClose={() => {
          handleTransferDialogClose();
          setViewOnlyMode(false);
        }}
        viewOnly={viewOnlyMode || (!!editingTransaction && !canEditTransactions())}
        onSwitchToEdit={() => setViewOnlyMode(false)}
        transactionIdPrefix={transactionIdPrefix}
        transactionIdNextSequence={transactionIdNextSequence}
        handleAccountChange={handleAccountChange}
        handleRefreshRate={handleRefreshRate}
        handleRefreshDefaultCurrencyRate={handleRefreshDefaultCurrencyRate}
        handleRefreshTransferRate={handleRefreshTransferRate}
        handleCreateDetailOption={handleCreateDetailOption}
        deriveCategoryDetails={deriveCategoryDetails}
        calculateTransferRate={calculateTransferRate}
        getConvertedAmount={getConvertedAmount}
        isTransactionFormValid={isTransactionFormValid}
        hasTransactionChanges={hasTransactionChanges}
        isTransferFormValid={isTransferFormValid}
        hasTransferChanges={hasTransferChanges}
        timeFormat={timeFormat}
        dateFormat={dateFormat}
        onAttachmentError={(msg) => {
          setSnackbarElement({ severity: "error", message: msg });
          setSnackbarOpen(true);
        }}
      />

      {/* Attachment Preview Dialog */}
      <AttachmentPreviewDialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewExpiresAt(null);
        }}
        previewUrl={previewUrl}
        previewName={previewName}
        previewType={previewType}
        previewExpiresAt={previewExpiresAt}
      />

      {/* Bottom Action Buttons - Fixed */}
      <Paper
        elevation={0}
        sx={{
          borderTop: 1,
          borderColor: "divider",
          p: 2,
          position: "sticky",
          bottom: "10px",
          zIndex: 10,
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
          <Button
            variant="outlined"
            color="error"
            startIcon={<AddIcon />}
            onClick={handleExpenseClick}
            sx={{ 
              flex: 1, 
              py: 1.5,
              borderWidth: 2,
              bgcolor: (theme) => `${theme.palette.error.main}14`,
              "&:hover": {
                bgcolor: (theme) => `${theme.palette.error.main}1F`,
                borderColor: (theme) => theme.palette.error.main,
                borderWidth: 2,
              }
            }}
          >
            Expense
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SwapHorizIcon />}
            onClick={handleTransferClick}
            sx={{ 
              flex: 1, 
              py: 1.5,
              borderWidth: 2,
              bgcolor: (theme) => `${theme.palette.primary.main}14`,
              "&:hover": {
                bgcolor: (theme) => `${theme.palette.primary.main}1F`,
                borderColor: (theme) => theme.palette.primary.main,
                borderWidth: 2,
              }
            }}
          >
            Transfer
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleIncomeClick}
            sx={{ 
              flex: 1, 
              py: 1.5,
              borderWidth: 2,
              bgcolor: (theme) => `${theme.palette.success.main}14`,
              "&:hover": {
                bgcolor: (theme) => `${theme.palette.success.main}1F`,
                borderColor: (theme) => theme.palette.success.main,
                borderWidth: 2,
              }
            }}
          >
            Income
          </Button>
        </Stack>
      </Paper>

      {/* Calendar Picker is now handled by DateRangePicker component */}
    </Box>
  );
};

export default Activity;
